/**
 * Cart Total Offer UI Component for Focal Theme
 * Handles visual display of progress bar and product selection
 * Version: 1.0.0
 */

class CartTotalOfferUI extends HTMLElement {
  constructor() {
    super();
    
    this.offerData = null;
    this.isUpdating = false;
    this.updateTimeout = null;
    this.handleOfferUpdate = this.handleOfferUpdate.bind(this);
    this.handleProductSelect = this.handleProductSelect.bind(this);
  }

  connectedCallback() {
    console.log('[CartTotalOfferUI] 🎯 Component connected');
    
    // Get DOM references
    this.container = this.querySelector('.cart-offer-container');
    this.progressContainer = this.querySelector('.progress-bar-container');
    this.progressBar = this.querySelector('.progress-bar-fill');
    this.progressMessage = this.querySelector('#progress-message');
    this.preMessage = this.querySelector('#pre-message');
    this.postMessage = this.querySelector('#post-message');
    this.productBlock = this.querySelector('.free-product-offer');
    this.productSelection = this.querySelector('#free-product-offer-block');
    this.scroller = this.querySelector('.product-selection-scroller-inner');
    this.prevArrow = this.querySelector('.product-selection-arrow--prev');
    this.nextArrow = this.querySelector('.product-selection-arrow--next');
    
    if (!this.container) {
      console.warn('[CartTotalOfferUI] ⚠️ Container not found');
      return;
    }

    // If refresh is in progress, hide product selection immediately
    if (window.CartTotalOfferGlobal && window.CartTotalOfferGlobal.isRefreshing) {
      console.log('[CartTotalOfferUI] 🔒 Hiding on connect (refresh in progress)');
      if (this.productSelection) {
        this.productSelection.style.opacity = '0';
        this.productSelection.style.pointerEvents = 'none';
      }
    }

    // Listen to offer updates
    document.addEventListener('cart-offer:updated', this.handleOfferUpdate);
    
    // Listen to product selection clicks
    const addButtons = this.querySelectorAll('.add-to-cart-btn');
    addButtons.forEach(btn => {
      btn.addEventListener('click', this.handleProductSelect);
    });
    
    // Initialize carousel navigation
    this.initializeCarousel();
    
    console.log('[CartTotalOfferUI] ✓ Component initialized');
  }

  disconnectedCallback() {
    document.removeEventListener('cart-offer:updated', this.handleOfferUpdate);
    console.log('[CartTotalOfferUI] Component disconnected');
  }

  /**
   * Handle offer update events
   */
  handleOfferUpdate(event) {
    console.log('[CartTotalOfferUI] 🔔 Offer updated');
    
    const {
      cartTotal,
      threshold,
      hasProduct,
      remainingAmount,
      progressPercentage,
      qualified,
      offer
    } = event.detail;
    
    this.offerData = event.detail;
    
    // Debounce UI updates to prevent flickering during cart refresh
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    // Small delay to allow cart refresh to complete
    this.updateTimeout = setTimeout(() => {
      this.updateUI();
    }, 300);
  }

  /**
   * Update UI based on current state
   */
  updateUI() {
    if (!this.offerData) return;
    
    // Skip updates during cart refresh to prevent flicker
    if (window.CartTotalOfferGlobal && window.CartTotalOfferGlobal.isRefreshing) {
      console.log('[CartTotalOfferUI] ⏭️ Skipping update (cart is refreshing)');
      
      // Hide product selection during refresh
      if (this.productSelection) {
        this.productSelection.style.opacity = '0';
        this.productSelection.style.pointerEvents = 'none';
      }
      return;
    }
    
    // Prevent rapid updates
    if (this.isUpdating) {
      console.log('[CartTotalOfferUI] ⏭️ Skipping update (already updating)');
      return;
    }
    
    this.isUpdating = true;
    
    const {
      hasProduct,
      remainingAmount,
      progressPercentage,
      qualified,
      offer
    } = this.offerData;

    console.log('[CartTotalOfferUI] 🎨 Updating UI:', {
      hasProduct,
      qualified,
      autoAdd: offer.auto_add
    });

    // Show/hide main container
    if (this.container) {
      this.container.style.display = 'block';
    }

    // Update progress bar
    if (this.progressBar) {
      this.progressBar.style.width = `${progressPercentage}%`;
    }

    // State 1: Has offer product (qualified)
    if (hasProduct) {
      this.showCompletedState();
      this.isUpdating = false;
      return;
    }

    // State 2: Qualified but no product yet (show selection or auto-added)
    if (qualified) {
      const isAutoAdd = offer.auto_add === 'true' || offer.auto_add === true || 
                        offer.auto_add_free_product === true || offer.auto_add_free_product === 'true';
      
      if (isAutoAdd) {
        // Auto-add mode - will be added automatically
        this.showCompletedState();
      } else {
        // Manual selection mode
        this.showSelectionState();
      }
      this.isUpdating = false;
      return;
    }

    // State 3: Not qualified yet (show progress)
    this.showProgressState(remainingAmount);
    this.isUpdating = false;
  }

  /**
   * Show progress state (not qualified yet)
   */
  showProgressState(remainingAmount) {
    const offer = this.offerData.offer;
    
    console.log('[CartTotalOfferUI] 📊 Showing progress state (not qualified)');
    
    // Show product block (it contains messages)
    if (this.productBlock) {
      this.productBlock.style.display = 'block';
    }
    
    // Show progress bar and message
    if (this.progressContainer) {
      this.progressContainer.style.display = 'block';
    }
    
    if (this.progressMessage && offer.text_progress_bar) {
      const message = offer.text_progress_bar.replace('[amount]', `₹${remainingAmount.toFixed(2)}`);
      this.progressMessage.textContent = message;
      this.progressMessage.style.display = 'block';
    }
    
    if (this.preMessage && offer.pre_message) {
      const message = offer.pre_message.replace('[amount]', `₹${remainingAmount.toFixed(2)}`);
      this.preMessage.textContent = message;
      this.preMessage.style.display = 'block';
      console.log('[CartTotalOfferUI] 💬 Showing pre-message:', message);
    } else {
      console.warn('[CartTotalOfferUI] ⚠️ Pre-message not configured:', {
        hasPreMessageElement: !!this.preMessage,
        hasPreMessageText: !!offer.pre_message
      });
    }
    
    if (this.postMessage) {
      this.postMessage.style.display = 'none';
    }
    
    // Hide product selection when not qualified - try multiple ways to ensure it's hidden
    const productSelectionBlock = this.querySelector('#free-product-offer-block');
    if (productSelectionBlock) {
      productSelectionBlock.style.display = 'none';
      console.log('[CartTotalOfferUI] 🚫 Hiding #free-product-offer-block (not qualified)');
    } else {
      console.log('[CartTotalOfferUI] ⚠️ #free-product-offer-block not found');
    }
    
    // Also hide via reference
    if (this.productSelection) {
      this.productSelection.style.display = 'none';
      console.log('[CartTotalOfferUI] 🚫 Hiding productSelection reference');
    }
  }

  /**
   * Show selection state (qualified, choose product)
   */
  showSelectionState() {
    const offer = this.offerData.offer;
    
    console.log('[CartTotalOfferUI] ✅ Showing selection state (qualified)');
    
    // Hide progress bar
    if (this.progressContainer) {
      this.progressContainer.style.display = 'none';
    }
    
    if (this.progressMessage) {
      this.progressMessage.style.display = 'none';
    }
    
    if (this.preMessage) {
      this.preMessage.style.display = 'none';
    }
    
    if (this.postMessage && offer.post_message) {
      this.postMessage.textContent = offer.post_message;
      this.postMessage.style.display = 'block';
    }
    
    // Show product block
    if (this.productBlock) {
      this.productBlock.style.display = 'block';
    }
    
    // Show product selection - query fresh in case it was removed
    const productSelectionBlock = this.querySelector('#free-product-offer-block');
    if (productSelectionBlock) {
      productSelectionBlock.style.display = 'block';
      productSelectionBlock.style.opacity = '1';
      productSelectionBlock.style.pointerEvents = 'auto';
      console.log('[CartTotalOfferUI] ✅ Showing #free-product-offer-block (qualified)');
      
      const addButtons = this.querySelectorAll('.add-to-cart-btn');
      addButtons.forEach(btn => {
        btn.disabled = false;
        btn.classList.remove('disabled');
      });
    } else {
      console.log('[CartTotalOfferUI] ⚠️ #free-product-offer-block not found for selection');
    }
    
    // Also update reference
    if (!this.productSelection) {
      this.productSelection = this.querySelector('#free-product-offer-block');
    }
  }

  /**
   * Show completed state (product added)
   */
  showCompletedState() {
    const offer = this.offerData.offer;
    
    // Show product block (it contains the post-message)
    if (this.productBlock) {
      this.productBlock.style.display = 'block';
    }
    
    // Hide progress message
    if (this.progressMessage) {
      this.progressMessage.style.display = 'none';
    }
    
    if (this.preMessage) {
      this.preMessage.style.display = 'none';
    }
    
    // Show completion message
    if (this.postMessage && offer.post_message) {
      this.postMessage.textContent = offer.post_message;
      this.postMessage.style.display = 'block';
    }
    
    // Hide progress bar
    if (this.progressContainer) {
      this.progressContainer.style.display = 'none';
    }
    
    // Remove product selection from DOM entirely
    if (this.productSelection && this.productSelection.parentNode) {
      console.log('[CartTotalOfferUI] 🗑️ Removing product selection from DOM');
      this.productSelection.remove();
      this.productSelection = null; // Clear reference
    }
  }

  /**
   * Initialize carousel navigation
   */
  initializeCarousel() {
    if (!this.scroller || !this.prevArrow || !this.nextArrow) {
      return;
    }

    // Handle arrow clicks
    this.prevArrow.addEventListener('click', () => this.scrollProducts('prev'));
    this.nextArrow.addEventListener('click', () => this.scrollProducts('next'));

    // Update arrow states on scroll
    if (this.scroller) {
      this.scroller.addEventListener('scroll', () => this.updateArrowStates());
      // Initial state
      this.updateArrowStates();
    }

    console.log('[CartTotalOfferUI] ✓ Carousel initialized');
  }

  /**
   * Scroll products in carousel
   */
  scrollProducts(direction) {
    if (!this.scroller) return;

    const scrollAmount = 156; // Item width (140px) + gap (12px) + padding (4px)
    const currentScroll = this.scroller.scrollLeft;
    
    if (direction === 'next') {
      this.scroller.scrollTo({
        left: currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    } else {
      this.scroller.scrollTo({
        left: currentScroll - scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Update arrow button states
   */
  updateArrowStates() {
    if (!this.scroller || !this.prevArrow || !this.nextArrow) return;

    const scrollLeft = this.scroller.scrollLeft;
    const scrollWidth = this.scroller.scrollWidth;
    const clientWidth = this.scroller.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    // Disable prev arrow at start
    this.prevArrow.disabled = scrollLeft <= 0;

    // Disable next arrow at end
    this.nextArrow.disabled = scrollLeft >= maxScroll - 1; // -1 for rounding
  }

  /**
   * Handle product selection click
   */
  async handleProductSelect(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    
    if (!variantId || !this.offerData) {
      console.error('[CartTotalOfferUI] ❌ Missing variant ID or offer data');
      return;
    }

    const offer = this.offerData.offer;
    
    // Disable button during processing
    button.disabled = true;
    button.textContent = 'Adding...';
    
    try {
      console.log('[CartTotalOfferUI] ➕ Adding selected product:', variantId);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              [offer.propery_name]: 'true'
            }
          }]
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add product');
      }

      console.log('[CartTotalOfferUI] ✅ Product added successfully');
      
      // Remove product selection from DOM immediately to prevent flicker
      if (this.productSelection && this.productSelection.parentNode) {
        console.log('[CartTotalOfferUI] 🗑️ Removing product selection after add');
        this.productSelection.remove();
        this.productSelection = null;
      }
      
      // Show completion message
      if (this.postMessage && this.offerData && this.offerData.offer) {
        this.postMessage.textContent = this.offerData.offer.post_message;
        this.postMessage.style.display = 'block';
      }
      
      // Refresh cart
      const cart = await fetch('/cart.js').then(r => r.json());
      
      if (window.location.href.indexOf('cart') > -1) {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart,
            openMiniCart: true,
            openDrawer: false
          }
        }));
      }
      
    } catch (error) {
      console.error('[CartTotalOfferUI] ❌ Error adding product:', error);
      button.disabled = false;
      button.textContent = 'Add to Cart';
      alert('Failed to add product. Please try again.');
    }
  }
}

// Register custom element
if (!customElements.get('cart-total-offer-ui')) {
  customElements.define('cart-total-offer-ui', CartTotalOfferUI);
  console.log('[CartTotalOfferUI] 🎨 Component registered ✓');
}

